import { useState, useEffect } from "react";
import { Space, Tooltip, message, Popconfirm, Button } from "antd";
import { useQuery, useMutation, useQueryClient } from "react-query";
import SkillsRequestsManagementService from "./SkillsRequestsManagementService";

const useSkillsRequestsManagement = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSkills, setFilteredSkills] = useState([]);

  const {
    data: skillsRequests = [],
    isLoading,
    error,
  } = useQuery(["skillsRequests"], SkillsRequestsManagementService.getSkillsRequestsList, {
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!skillsRequests) return;
  
    if (!searchQuery.trim()) {
      setFilteredSkills(skillsRequests.filter((skill) => !skill.isActivated));
    } else {
      const searchTerm = searchQuery.toLowerCase();
  
      setFilteredSkills(
        skillsRequests
          .filter((skill) =>
            !skill.isActivated &&
            (
              skill.name.toLowerCase().includes(searchTerm) ||
              skill.categories?.some(
                (cat) =>
                  cat.categoryName?.toLowerCase().includes(searchTerm) ||
                  cat.subCategory?.toLowerCase().includes(searchTerm) ||
                  cat.subSubCategory?.toLowerCase().includes(searchTerm)
              )
            )
          )
      );
    }
  }, [searchQuery, skillsRequests]);
  
  const activateSkillMutation = useMutation(SkillsRequestsManagementService.activateSkill, {
    onSuccess: () => {
      queryClient.invalidateQueries("skillsRequests");
      message.success("Skill activated successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.message || "Failed to activate skill.");
    },
  });

  const handleActivateSkill = async (id) => {
    await activateSkillMutation.mutateAsync(id);
  };
  const deleteSkillMutation = useMutation(SkillsRequestsManagementService.deleteSkill, {
    onSuccess: () => {
      queryClient.invalidateQueries("skillsRequests");
      message.success("Skill deleted successfully");
    },
    onError: (error) => {
      if (error.response?.status === 400) {
        message.error("This skill cannot be deleted because it is assigned to users.");
      } else {
        message.error("Failed to delete skill.");
      }
    },
  });
  
  const handleDeleteSkill = async (id) => {
    await deleteSkillMutation.mutateAsync(id);
  };
  

  const columns = [
    {
      title: "Skill Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Categories",
      dataIndex: "categories",
      key: "categories",
      render: (categories) =>
        categories && categories.length > 0
          ? categories
              .map((cat) => {
                let formatted = cat.categoryName;
                if (cat.subCategory) formatted += ` > ${cat.subCategory}`;
                if (cat.subSubCategory) formatted += ` > ${cat.subSubCategory}`;
                return formatted;
              })
              .join(" | ")
          : "N/A",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          <Popconfirm
            title="Are you sure you want to activate this skill?"
            onConfirm={() => handleActivateSkill(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Activate Skill">
              <Button type="primary">Activate</Button>
            </Tooltip>
          </Popconfirm>
    
          <Popconfirm
            title="Are you sure you want to delete this skill?"
            onConfirm={() => handleDeleteSkill(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Skill">
              <Button danger type="default">Delete</Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    }
    
  ];

  return {
    skillsRequests: filteredSkills,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    columns,
  };
};

export default useSkillsRequestsManagement;
